import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Fragment } from 'react/cjs/react.production.min';
import DraftTrackerDraftedPlayerCard from '../../DraftTracker/DraftTrackerDraftedPlayerCard/DraftTrackerDraftedPlayerCard';
import PlayerProfileCard from '../../Player/PlayerProfileCard/PlayerProfileCard';
import TeamProfileCard from '../../Team/TeamProfileCard/TeamProfileCard';
import StatusWidget from '../../StatusWidget/StatusWidget';
import RosterPlayerNoteModal from '../RosterPlayerNoteModal/RosterPlayerNoteModal';
import RosterPortraitCard from '../RosterPortraitCard/RosterPortraitCard';
import FixedRosterBanner from '../FixedRosterBanner/FixedRosterBanner';
import ChampionshipWeeksModal from '../../Player/ChampionshipWeeksModal/ChampionshipWeeksModal';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

const RosterDraftCard = ({
	get_roster_by_user_endpoint,
	get_player_endpoint,
	get_players_endpoint,
	get_team_endpoint,
	get_roster_player_note_endpoint,
	create_roster_player_note_endpoint,
	update_roster_player_note_endpoint,
	get_notes_by_roster_endpoint,
	update_notes_by_roster_endpoint,
	get_draft_notes_by_roster_endpoint,
	update_draft_notes_by_roster_endpoint,
	get_lessons_learned_notes_by_roster_endpoint,
	update_lessons_learned_notes_by_roster_endpoint,
	selectedRoster,
	isDraftModeEnabled
}) => {
	const { user } = useAuth();
	const { id } = useParams();
	const modalRef = useRef();
	const [
		roster,
		setRoster
	] = useState(null);
	const [
		selectedPlayer,
		setSelectedPlayer
	] = useState([]);
	const [
		isPlayerModalOpen,
		setIsPlayerModalOpen
	] = useState(false);
	const [
		selectedTeam,
		setSelectedTeam
	] = useState([]);
	const [
		isTeamModalOpen,
		setIsTeamModalOpen
	] = useState(false);
	const [
		isRosterPlayerNoteModalOpen,
		setIsRosterPlayerNoteModalOpen
	] = useState(false);
	const [
		scrolledPastBanner,
		setScrolledPastBanner
	] = useState(false);
	const [
		isRosterPlayersSectionOpen,
		setIsRosterPlayerSectionOpen
	] = useState(true);
	const [
		isChampionshipWeeksModalOpen,
		setIsChampionshipWeeksModalOpen
	] = useState(false);

	useEffect(
		() => {
			if (get_roster_by_user_endpoint) {
				const endpoint = get_roster_by_user_endpoint
					.replace('user_id', user.id)
					.replace('roster_id', selectedRoster ? selectedRoster.id : id);
				fetch(endpoint).then((response) => response.json()).then((data) => {
					setRoster(data.data);
				});
			}
		},
		[
			get_roster_by_user_endpoint,
			user.id,
			selectedRoster,
			id
		]
	);

	const handleOpenRosterPlayersSection = () => {
		setIsRosterPlayerSectionOpen(!isRosterPlayersSectionOpen);
	};

	const handleOpenRosterPlayerNote = (player) => {
		setSelectedPlayer(player);
		setIsRosterPlayerNoteModalOpen(true);
	};

	const handleOpenChampionshipWeeksModal = (player) => {
		setSelectedPlayer(player);
		setIsChampionshipWeeksModalOpen(true);
	};

	const handlePlayerClick = (player) => {
		setSelectedPlayer(player);
		setIsPlayerModalOpen(true);
	};

	const handleTeamClick = (team) => {
		setSelectedTeam(team);
		setIsTeamModalOpen(true);
	};

	const closePlayerModal = () => {
		setIsPlayerModalOpen(false);
		setSelectedPlayer(null);
	};

	const closeTeamModal = () => {
		setIsTeamModalOpen(false);
		setSelectedTeam(null);
	};

	const closeChampionshipWeeksModal = () => {
		setIsChampionshipWeeksModalOpen(false);
		setSelectedPlayer(null);
	};

	const closePlayerModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsPlayerModalOpen(false);
			setSelectedPlayer(null);
		}
	};

	const closeTeamModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsTeamModalOpen(false);
			setSelectedTeam(null);
		}
	};

	const closeRosterPlayerNoteModalOnClickOutside = (event) => {
		if (!modalRef.current.contains(event.target)) {
			setIsRosterPlayerNoteModalOpen(false);
		}
	};


	useEffect(
		() => {
			if (isPlayerModalOpen) {
				document.addEventListener('mousedown', closePlayerModalOnClickOutside);
			} else {
				document.removeEventListener(
					'mousedown',
					closePlayerModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closePlayerModalOnClickOutside
				);
			};
		},
		[
			isPlayerModalOpen
		]
	);

	useEffect(
		() => {
			if (isTeamModalOpen) {
				document.addEventListener('mousedown', closeTeamModalOnClickOutside);
			} else {
				document.removeEventListener('mousedown', closeTeamModalOnClickOutside);
			}
			return () => {
				document.removeEventListener('mousedown', closeTeamModalOnClickOutside);
			};
		},
		[
			isTeamModalOpen
		]
	);

	useEffect(
		() => {
			if (isRosterPlayerNoteModalOpen) {
				document.addEventListener(
					'mousedown',
					closeRosterPlayerNoteModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closeRosterPlayerNoteModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closeRosterPlayerNoteModalOnClickOutside
				);
			};
		},
		[
			isRosterPlayerNoteModalOpen
		]
	);

	useEffect(() => {
		const handleScroll = () => {
			const bannerHeight = document.querySelector('.banner-section')
				.offsetHeight;
			const scrolled = window.scrollY > bannerHeight;
			setScrolledPastBanner(scrolled);
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	if (!roster) {
		return (
			<div>
				<LoadingSpinner />
			</div>
		);
	}

	const renderPlayers = (position, players) => (
		<div className="mx-6 inline-block py-6 w-72">
			<h3
				className="text-xl text-gray-800 font-bold"
			>
				{position.toUpperCase()}
			</h3>
			<div>
				{players.length === 0 ? (
					<p className="text-gray-500 flex justify-start pt-6">
						No {position.toLowerCase()} drafted yet.
					</p>
				) : (
					players.map((player, index) => (
						<div key={`${player.id}-${player.position}-${index}`}>
							<DraftTrackerDraftedPlayerCard
								player={player}
								handlePlayerClick={handlePlayerClick}
								handleTeamClick={handleTeamClick}
								handleOpenRosterPlayerNote={handleOpenRosterPlayerNote}
								handleOpenChampionshipWeeksModal={
									handleOpenChampionshipWeeksModal
								}
							/>
						</div>
					))
				)}
			</div>
		</div>
	);

	return (
		<div>
			<div className="banner-section">
				<RosterPortraitCard
					roster={roster}
					isDraftModeEnabled={isDraftModeEnabled}
					handleOpenRosterPlayersSection={handleOpenRosterPlayersSection}
					isRosterPlayersSectionOpen={isRosterPlayersSectionOpen}
					get_notes_by_roster_endpoint={get_notes_by_roster_endpoint}
					update_notes_by_roster_endpoint={update_notes_by_roster_endpoint}
					get_draft_notes_by_roster_endpoint={
						get_draft_notes_by_roster_endpoint
					}
					update_draft_notes_by_roster_endpoint={
						update_draft_notes_by_roster_endpoint
					}
					get_lessons_learned_notes_by_roster_endpoint={
						get_lessons_learned_notes_by_roster_endpoint
					}
					update_lessons_learned_notes_by_roster_endpoint={
						update_lessons_learned_notes_by_roster_endpoint
					}
				/>
			</div>

			{/* Fixed Roster Banner  - Displays after scrolling past the Roster Portrait Card Section */}
			{scrolledPastBanner && (
				<FixedRosterBanner
					scrolledPastBanner={scrolledPastBanner}
					roster={roster}
				/>
			)}

			<div className="relative flex items-start">
				{roster.players &&
				isRosterPlayersSectionOpen && (
					<Fragment>
						{renderPlayers('Quarterbacks', roster.players.quarterbacks)}
						{renderPlayers('Running Backs', roster.players.running_backs)}
						{renderPlayers('Wide Receivers', roster.players.wide_receivers)}
						{renderPlayers('Tight Ends', roster.players.tight_ends)}
					</Fragment>
				)}
			</div>

			{/* Player Modal */}
			{isPlayerModalOpen &&
			selectedPlayer && (
				<div
					className="fixed inset-0 z-50 overflow-auto flex w-full"
					style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
				>
					<div
						className="relative p-8 bg-white m-auto flex-col flex rounded-lg"
						style={{ width: '75%' }}
						ref={modalRef}
					>
						<button
							className="absolute top-0 right-0 p-2 text-gray-700 hover:text-gray-900"
							onClick={closePlayerModal}
						>
							<svg
								className="h-6 w-6 fill-current"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M6.225 4.811l.084.073L12 10.587l5.691-5.691a1 1 0 011.497 1.32l-.083.094L13.413 12l5.692 5.692a1 1 0 01-1.32 1.497l-.094-.083L12 13.413l-5.691 5.691a1 1 0 01-1.497-1.32l.083-.094L10.587 12 4.811 6.225a1 1 0 011.32-1.497z" />
							</svg>
						</button>
						<PlayerProfileCard
							player={selectedPlayer}
							get_player_endpoint={get_player_endpoint}
							get_players_endpoint={get_players_endpoint}
						/>
					</div>
				</div>
			)}

			{/* Team Modal */}
			{isTeamModalOpen &&
			selectedTeam && (
				<div
					className="fixed inset-0 z-50 overflow-auto flex w-full"
					style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
				>
					<div
						className="relative p-8 bg-white m-auto flex-col flex rounded-lg"
						style={{ width: '75%' }}
						ref={modalRef}
					>
						<button
							className="absolute top-0 right-0 p-2 text-gray-700 hover:text-gray-900"
							onClick={closeTeamModal}
						>
							<svg
								className="h-6 w-6 fill-current"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M6.225 4.811l.084.073L12 10.587l5.691-5.691a1 1 0 011.497 1.32l-.083.094L13.413 12l5.692 5.692a1 1 0 01-1.32 1.497l-.094-.083L12 13.413l-5.691 5.691a1 1 0 01-1.497-1.32l.083-.094L10.587 12 4.811 6.225a1 1 0 011.32-1.497z" />
							</svg>
						</button>
						<TeamProfileCard
							team={selectedTeam}
							get_team_endpoint={get_team_endpoint}
						/>
					</div>
				</div>
			)}

			{/* Roster Player Note Modal */}
			{isRosterPlayerNoteModalOpen && (
				<RosterPlayerNoteModal
					userId={user.id}
					rosterId={roster.id}
					player={selectedPlayer}
					isRosterPlayerNoteModalOpen={isRosterPlayerNoteModalOpen}
					closeRosterPlayerNoteModal={() =>
						setIsRosterPlayerNoteModalOpen(false)}
					get_roster_player_note_endpoint={get_roster_player_note_endpoint}
					create_roster_player_note_endpoint={
						create_roster_player_note_endpoint
					}
					update_roster_player_note_endpoint={
						update_roster_player_note_endpoint
					}
					modalRef={modalRef}
				/>
			)}

			{/* Championship Weeks Modal */}
			<ChampionshipWeeksModal
				isOpen={isChampionshipWeeksModalOpen}
				onClose={closeChampionshipWeeksModal}
				player={selectedPlayer}
			/>
		</div>
	);
};

export default RosterDraftCard;
